/**
 * Utility functions for parsing CSV files
 */

export interface ParsedCSV<T = Record<string, any>> {
    headers: string[];
    data: T[];
    errors: string[];
}

/**
 * Parse CSV text content into structured data
 */
export function parseCSV(content: string, expectedHeaders?: string[]): ParsedCSV {
    const lines = content.split(/\r?\n/).filter((line) => line.trim());

    if (lines.length === 0) {
        return { headers: [], data: [], errors: ['Plik jest pusty'] };
    }

    const errors: string[] = [];

    // Parse header row
    const headers = parseCSVRow(lines[0]);

    // Validate expected headers if provided
    if (expectedHeaders) {
        const missingHeaders = expectedHeaders.filter(
            (h) => !headers.some((header) => header.toLowerCase() === h.toLowerCase())
        );
        if (missingHeaders.length > 0) {
            errors.push(`Brakujące kolumny: ${missingHeaders.join(', ')}`);
        }
    }

    // Parse data rows
    const data: Record<string, any>[] = [];
    for (let i = 1; i < lines.length; i++) {
        const values = parseCSVRow(lines[i]);
        if (values.length !== headers.length) {
            errors.push(`Wiersz ${i + 1}: nieprawidłowa liczba kolumn (${values.length} vs ${headers.length})`);
            continue;
        }

        const row: Record<string, any> = {};
        headers.forEach((header, index) => {
            row[header.trim()] = values[index]?.trim() || '';
        });
        data.push(row);
    }

    return { headers, data, errors };
}

/**
 * Parse a single CSV row handling quoted values
 */
function parseCSVRow(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];

        if (char === '"') {
            if (inQuotes && nextChar === '"') {
                current += '"';
                i++; // Skip the next quote
            } else {
                inQuotes = !inQuotes;
            }
        } else if ((char === ',' || char === ';') && !inQuotes) {
            result.push(current);
            current = '';
        } else {
            current += char;
        }
    }
    result.push(current);

    return result;
}

/**
 * Map CSV row to product entity format
 */
export function mapCSVToProduct(row: Record<string, any>): Record<string, any> {
    return {
        sku: row['SKU'] || row['sku'] || '',
        name: row['Nazwa'] || row['name'] || row['Name'] || '',
        shortName: row['Nazwa skrócona'] || row['Nazwa skrocona'] || row['shortName'] || row['Short name'] || '',
        description: row['Opis'] || row['description'] || row['Description'] || '',
        category: row['Kategoria'] || row['category'] || row['Category'] || '',
        basePrice: parseFloat(row['Cena'] || row['Cena bazowa'] || row['basePrice'] || row['price'] || '0') || 0,
        costPrice: parseFloat(row['Koszt'] || row['Cena zakupu'] || row['costPrice'] || row['cost'] || '0') || 0,
        barcode: row['Kod kreskowy'] || row['barcode'] || row['EAN'] || '',
        brand: row['Marka'] || row['brand'] || row['Brand'] || '',
        unit: row['Jednostka'] || row['unit'] || 'szt',
        isActive: row['Aktywny']?.toLowerCase() !== 'nie' && row['isActive'] !== 'false',
    };
}
